/**
 * @file The frame loop.
 *
 * Owns `requestAnimationFrame`, the clamped delta and the FPS average, and
 * nothing else — it does not know what a player or an enemy is. Whoever starts
 * it supplies an update and a render callback.
 */

export class Engine {
  /**
   * @param {object} options Engine setup.
   * @param {(dt: number) => void} options.update Simulation step.
   * @param {() => void} options.render Draw step.
   * @param {import('./Context.js').TimeInfo} options.time Time object to keep updated.
   * @param {number} options.maxDelta Largest delta a frame may report, in seconds.
   * @param {number} options.fpsSampleWindow Averaging window for the FPS readout.
   */
  constructor({ update, render, time, maxDelta, fpsSampleWindow }) {
    /** @type {(dt: number) => void} @private */
    this._update = update;
    /** @type {() => void} @private */
    this._render = render;
    /** @type {import('./Context.js').TimeInfo} */
    this.time = time;
    /** @type {number} @private */
    this._maxDelta = maxDelta;
    /** @type {number} @private */
    this._fpsWindow = fpsSampleWindow;

    /** @type {boolean} */
    this.running = false;
    /** @type {number} @private */
    this._rafId = 0;
    /** @type {number} @private */
    this._lastTime = 0;
    /** @type {number} @private */
    this._fpsAccumulator = 0;
    /** @type {number} @private */
    this._fpsFrames = 0;

    /** @type {(now: number) => void} @private */
    this._tick = (now) => this._onFrame(now);
  }

  /**
   * Starts the loop. Calling it while running is a no-op.
   * @returns {void}
   */
  start() {
    if (this.running) return;
    this.running = true;
    this._lastTime = performance.now();
    this._rafId = requestAnimationFrame(this._tick);
  }

  /**
   * Stops the loop.
   * @returns {void}
   */
  stop() {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this._rafId);
  }

  /**
   * @param {number} now High-resolution timestamp from rAF.
   * @returns {void}
   * @private
   */
  _onFrame(now) {
    if (!this.running) return;
    this._rafId = requestAnimationFrame(this._tick);

    const rawDelta = (now - this._lastTime) / 1000;
    this._lastTime = now;

    // A tab that was in the background reports a huge delta; clamping it stops
    // entities teleporting through walls on the first frame back.
    const delta = Math.min(rawDelta, this._maxDelta);

    this.time.delta = delta;
    this.time.elapsed += delta;
    this.time.frame++;
    this._trackFps(rawDelta);

    this._update(delta);
    this._render();
  }

  /**
   * Averages FPS over a window so the readout is stable enough to read.
   * @param {number} rawDelta Unclamped delta in seconds.
   * @returns {void}
   * @private
   */
  _trackFps(rawDelta) {
    this._fpsAccumulator += rawDelta;
    this._fpsFrames++;
    if (this._fpsAccumulator < this._fpsWindow) return;

    this.time.fps = Math.round(this._fpsFrames / this._fpsAccumulator);
    this._fpsAccumulator = 0;
    this._fpsFrames = 0;
  }
}
